import { looksLikeALink } from "@cuekards/core";
import type { Settings } from "@cuekards/core";

/**
 * Settings as a file, so a rig can move between two browsers (story 59).
 *
 * There are no accounts and no server-side per-user state — that is a
 * requirement of the spec rather than a default — so settings are per browser
 * profile, not per person. This is one of the two mitigations that need no
 * server. The other is `./settings-url.ts`, and they carry different things on
 * purpose: **a file carries everything**, because its job is "restore this
 * browser exactly", while a URL carries the handful of values that mean
 * something on somebody else's machine.
 *
 * The format is the settings blob with an envelope around it, and the envelope
 * is the whole of the design. Writing the bare blob would be shorter and would
 * make a dropped `package.json` — or any other JSON file — read as a valid,
 * nearly empty settings export, which is to say it would replace a working setup
 * with nothing and report success. So a file has to say it is one of ours before
 * a single value is taken from it.
 */

/** What the envelope says it is. Checked on the way in, which is what makes the
 * wrong file an explanation rather than a wiped setup. */
export const SETTINGS_FILE_KIND = "cuekards-settings";

/**
 * The format's version, which exists to be *refused* rather than migrated.
 *
 * There is one version and there may never be a second, but a file written by a
 * later cuekards is the one case where reading it anyway is worse than not: the
 * blob is the core's and grows with it, and a future field this version does not
 * understand is a field it would silently keep and then hand to a store whose
 * accessors know nothing about it. Saying "this came from a newer cuekards" costs
 * one number and is the difference between a puzzling setup and an explained
 * one.
 */
export const SETTINGS_FILE_VERSION = 1;

/** The envelope, as it is written to disk. */
interface SettingsFile {
  cuekards: typeof SETTINGS_FILE_KIND;
  version: number;
  /** When it was written. Nothing reads it — it is there for whoever opens the
   * file in a text editor wondering which of two exports is the newer. */
  exported: string;
  settings: Settings;
}

/** What came of trying to read one. */
export type SettingsFileRead =
  | { ok: true; settings: Settings }
  /** Why this file is not going to be imported, in words for the person who
   * chose it. */
  | { ok: false; message: string };

/**
 * The settings with a pasted script taken out, and nothing else touched.
 *
 * On the web `lastDocUrl` holds whatever was last loaded, and for a pasted script
 * that is the script itself (ADR-0039). A settings file gets copied between
 * machines and sent to other people, and the privacy page says the script stays in
 * this browser, so the text never goes into a file — and never comes out of one
 * either, so an export written before this rule cannot put a script back.
 *
 * **A link stays.** It is the address of a document its owner already shares by
 * link, cuekards fetches it fresh, and restoring it is part of restoring this
 * browser. Which is which is `looksLikeALink`, the same test the room uses to put a
 * remembered value back into the link box or the script box, so the file and the
 * screen cannot disagree about what a value is.
 */
function withoutPastedScript(settings: Settings): Settings {
  const { lastDocUrl, ...rest } = settings;
  return lastDocUrl !== undefined && looksLikeALink(lastDocUrl)
    ? { ...rest, lastDocUrl }
    : rest;
}

/**
 * The file's text, for a rig.
 *
 * The settings go in as they are, including keys this version has never heard
 * of — **with the one exception of a pasted script**, see
 * {@link withoutPastedScript}. Copying only the known keys would be the same
 * mistake as parsing only the known ones on the way back: the blob belongs to the
 * core and grows with it, and an export written by a cuekards one ticket older
 * than the one that reads it must not lose whatever the newer one added.
 *
 * Indented, because a settings file is a thing a person may well open.
 */
export function settingsFileText(
  settings: Settings,
  now: Date = new Date(),
): string {
  const file: SettingsFile = {
    cuekards: SETTINGS_FILE_KIND,
    version: SETTINGS_FILE_VERSION,
    exported: now.toISOString(),
    settings: withoutPastedScript(settings),
  };
  return `${JSON.stringify(file, null, 2)}\n`;
}

/** What to call it. Dated rather than timestamped: the browser appends its own
 * `(1)` when two land in the same folder on the same day, and a filename with
 * seconds in it is unreadable at the moment it matters, which is picking one of
 * several out of a downloads list. */
export function settingsFileName(now: Date = new Date()): string {
  return `cuekards-settings-${now.toISOString().slice(0, 10)}.json`;
}

/**
 * A file's text as settings, or the reason it is not going to be.
 *
 * Every refusal names the file rather than the field, because the mistake this
 * catches is nearly always "that was the wrong file" and never "field seven is
 * malformed". Note the asymmetry with {@link import("@cuekards/core").parseSettings}:
 * that one reads anything unreadable as empty settings so a corrupt store cannot
 * stop the app starting, and this one refuses, because an import that quietly
 * did nothing would be indistinguishable from one that worked.
 */
export function readSettingsFile(text: string): SettingsFileRead {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return {
      ok: false,
      message:
        "That is not a settings file — it is not even JSON. Choose the file " +
        "cuekards exported, which is named cuekards-settings-<date>.json.",
    };
  }

  if (
    typeof parsed !== "object" ||
    parsed === null ||
    (parsed as SettingsFile).cuekards !== SETTINGS_FILE_KIND
  ) {
    return {
      ok: false,
      message:
        "That is not a cuekards settings file. Choose the one cuekards exported, " +
        "named cuekards-settings-<date>.json.",
    };
  }

  const file = parsed as SettingsFile;
  if (typeof file.version === "number" && file.version > SETTINGS_FILE_VERSION) {
    return {
      ok: false,
      message:
        `That file was written by a newer version of cuekards (format ` +
        `${file.version}, and this one reads ${SETTINGS_FILE_VERSION}). ` +
        `Export it again from this browser's version, or update this one.`,
    };
  }

  const settings = file.settings;
  if (typeof settings !== "object" || settings === null || Array.isArray(settings)) {
    return {
      ok: false,
      message:
        "That settings file has no settings in it. Export again from the " +
        "browser you are copying from.",
    };
  }

  return { ok: true, settings: withoutPastedScript(settings) };
}
